// Registro de asistencia (card)

import React, { use, useEffect, useState } from 'react';
import { useUser } from '../UserContext';
import {
  Button,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Stack,
  Grid,
  TextField,
  MenuItem,
} from "@mui/material";
import { apiFetchJson } from '../fetchWrapper';
import MapWidget from './MapWidget';
import { getAttendanceStatusLabel } from './AttendanceStatus';
import { getShiftStart } from '../util/ShiftUtil';

export default function RegisterAttendanceCard({ nextShift, nextShiftAttendance, setNextShiftAttendance, selectedEmployee, manualAttendance, allowManualAttendance }) {
  const { user } = useUser();
  const [site, setSite] = useState({});
  const [shiftTemplate, setShiftTemplate] = useState({});
  const [openAbsence, setOpenAbsence] = useState(false);
  const [absenceReason, setAbsenceReason] = useState('');
  const [manualAttendanceRecord, setManualAttendanceRecord] = useState(null);

  useEffect(() => {
    if (!nextShift?.siteId) return;
    apiFetchJson(`/api/sites/${nextShift.siteId}`).then(setSite);
  }, [nextShift]);

  useEffect(() => {
    if (!nextShift?.shiftTemplateId) return;
    apiFetchJson("/api/shift-templates/" + nextShift.shiftTemplateId).then(setShiftTemplate)
  }, [nextShift]);

  const effectiveEmployee = selectedEmployee || user;
  const attendanceRecord = nextShiftAttendance || manualAttendanceRecord;
  const checkInAvailable = !attendanceRecord && (!!nextShift || allowManualAttendance);
  const checkOutAvailable = attendanceRecord && !attendanceRecord?.checkOut;

  const handleAbsent = (shift) => {
    console.log("Absent:", shift);
  }

  const getAttendanceStatus = (shift) => {
    if (!shift) {
      return 'ON_TIME';
    }

    const now = new Date();
    const shiftStart = getShiftStart(shift, [shiftTemplate]);
    if (!shiftStart) {
      return 'ON_TIME';
    }

    const shiftStartTs = new Date(shiftStart.getTime());
    const diff = now - shiftStartTs;

    if (diff <= 5 * 60 * 1000) { // a tiempo
      return 'ON_TIME';
    } else if (diff <= 15 * 60 * 1000) { // retraso dentro los primeros 15 min.
      return 'LATE';
    } else {
      return 'NO_SHOW';
    }
  }

  const handleCheckIn = (shift) => {
    if (!navigator.geolocation) {
      alert("Geolocalización no soportada en este navegador");
      return;
    }

    if (!effectiveEmployee?.id) {
      alert("No se ha encontrado el empleado para registrar la asistencia");
      return;
    }

    if (manualAttendance && !selectedEmployee?.id) {
      alert("Selecciona un empleado para registrar asistencia manual");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const atStatus = getAttendanceStatus(shift);
        const payload = {
          scheduledShiftId: shift?.id ?? null,
          employeeId: selectedEmployee?.id ?? effectiveEmployee?.id ?? null,
          attendanceStatus: atStatus,
          latitude,
          longitude,
          checkIn: new Date().toISOString(),
        };

        await apiFetchJson("/api/attendances", {
          method: "POST",
          body: JSON.stringify(payload),
        }).then(res => { console.log("Check-in response:", res); return res; })
          .then(updated => {
            if (shift) {
              setNextShiftAttendance(updated);
            } else {
              setManualAttendanceRecord(updated);
            }
          });
      });
  }

  const handleCheckOut = async (shift) => {
    const attendanceToUpdate = attendanceRecord;
    if (!attendanceToUpdate || !attendanceToUpdate.checkIn) {
      alert("No se ha registrado un check-in para este turno");
      return;
    }
    const payload = {
      ...attendanceToUpdate,
      checkOut: new Date().toISOString(),
    };
    await apiFetchJson("/api/attendances/" + attendanceToUpdate.id, {
      method: "PUT",
      body: JSON.stringify(payload),
    }).then(res => { console.log("Check-out response:", res); return res; })
      .then(updated => {
        if (shift) {
          setNextShiftAttendance(updated);
        } else {
          setManualAttendanceRecord(updated);
        }
      });
  }

  const showCard = !!nextShift || manualAttendance || allowManualAttendance;
  const shiftDateLabel = nextShift?.shiftDate
    ? new Date(nextShift.shiftDate).toLocaleDateString()
    : manualAttendance ? new Date().toLocaleDateString() : '—';
  const shiftName = nextShift
    ? shiftTemplate?.name || nextShift.shiftTemplateId
    : manualAttendance ? 'Registro manual' : '—';

  const employeePerson = effectiveEmployee?.user?.person || effectiveEmployee?.person || null;
  const employeeName = employeePerson
    ? `${employeePerson.firstname || ''} ${employeePerson.lastnameFirst || ''}`.trim()
    : 'Empleado no seleccionado';
  const attendanceToShow = attendanceRecord;

  return (
    <section>
      <Grid container spacing={2} >

        {/* registro */}
        <Grid size={{ xs: 12, sm: 6 }}>

          {showCard && (
            <Card
              elevation={3}
              sx={{
                height: 400,
                borderRadius: 3,
                backgroundColor: '#eef0f2',
                color: 'black',
                p: 1,
              }}
            >
              <CardContent>
                <Stack spacing={1.5}>

                  <Typography variant="h6" color="primary">
                    Registro de turno
                  </Typography>

                  {/* info */}
                  <Box>
                    {[
                      { label: 'Fecha:', value: shiftDateLabel },
                      { label: 'Empleado:', value: employeeName },
                      { label: 'Ubicación:', value: site?.name || '—' },
                      { label: 'Turno:', value: shiftName },
                    ].map((item) => (
                      <Box
                        key={item.label}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          borderBottom: '1px solid #ddd',
                          gap: 1,
                          py: 0.5,
                        }}
                      >
                        <Typography sx={{ fontWeight: "bold", minWidth: 120 }} >{item.label}</Typography>

                        <Typography>{item.value}</Typography>
                      </Box>
                    ))}
                  </Box>

                  {/* botones */}
                  <Stack direction="row" spacing={2} mt={2}>

                    <Button
                      fullWidth
                      variant="contained"
                      color="success"
                      disabled={!checkInAvailable}
                      onClick={() => handleCheckIn(nextShift)}
                    >
                      Entrada
                    </Button>

                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      disabled={!checkOutAvailable}
                      onClick={() => handleCheckOut(nextShift)}
                    >
                      Salida
                    </Button>
                  </Stack>

                  <Stack direction="row" spacing={2} mt={2}></Stack>

                  <Button
                    fullWidth
                    variant='outlined'
                    color='warning'
                    onClick={() => setOpenAbsence(true)}
                  >
                    Inasistencia
                  </Button>

                </Stack>

                <Dialog
                  open={openAbsence}
                  onClose={() => setOpenAbsence(false)}
                  fullWidth
                >

                  <DialogTitle> Marcar Inasistencia</DialogTitle>

                  <DialogContent>
                    <TextField
                      select
                      fullWidth
                      label="motivo"
                      value={absenceReason}
                      onChange={(e) => setAbsenceReason(e.target.value)}
                      sx={{ mt: 2 }}
                    >
                      <MenuItem value="sick">Enfermedad</MenuItem>
                      <MenuItem value="personal">Personal</MenuItem>
                      <MenuItem value="transport">Transporte</MenuItem>
                      <MenuItem value="no_show">No se presentó</MenuItem>
                      <MenuItem value="other">Otro</MenuItem>
                    </TextField>

                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Observaciones (opcional)"
                      sx={{ mt: 2 }}
                    />

                  </DialogContent>

                  <DialogActions>
                    <Button onClick={() => setOpenAbsence(false)} color="error">
                      Cancelar
                    </Button>

                    <Button
                      variant='contained'
                      color='warning'
                      onClick={() => {
                        handleSaveAbsence();
                        setOpenAbsence(false);
                      }}
                      disabled={!absenceReason}
                    >
                      Guardar
                    </Button>

                  </DialogActions>
                </Dialog>
                
                  <Box sx={{ mt: 2 }}
                  >
                    {attendanceToShow ? (
                      <Box sx={{ p: 1, bgcolor: '#d5d4d4', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Comprobante
                        </Typography>

                        <Typography>
                          <strong>Estatus:</strong> {getAttendanceStatusLabel(attendanceToShow.attendanceStatus)}
                        </Typography>

                        <Typography>
                          <strong> Entrada:</strong>{" "}
                          {attendanceToShow.checkIn
                            ? new Date(attendanceToShow.checkIn).toLocaleString()
                            : "—"}
                        </Typography>

                        <Typography>
                          <strong>Salida:</strong>{" "}
                          {attendanceToShow.checkOut
                            ? new Date(attendanceToShow.checkOut).toLocaleString()
                            : "—"}
                        </Typography>

                      </Box>
                    ) : (
                      <Typography color="text.secondary" mt={3}>
                        {manualAttendance ? 'No se ha iniciado la asistencia manual para este empleado.' : 'Este turno aún no ha sido iniciado'}
                      </Typography>
                    )}
                  </Box>
                
              </CardContent>
            </Card>
          )}
        </Grid >

        <Grid
          size={{ xs: 12, sm: 6 }}
          sx={{ order: { xs: 2, sm: 2 } }}
        >

          <Card elevation={3}
            sx={{
              height: 400,
              p: 2,
              borderRadius: 3,
              backgroundColor: '#eef0f2',
              color: 'black',
            }}
          >
            <MapWidget
              hideTitle
              withCard={false}
              site={site}
              height={370}
            />
          </Card>
        </Grid>
      </Grid>
    </section>
  );
}